import React from 'react';
import Needs from '@utils/NeedsClass';
import { WrapperEntitySelector, QueryPaginatedSelection, Select } from '@components/Inputs';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { withTranslation } from 'react-i18next';
import { Grid, TextInput, IconButton, Icon } from '@ts/storybook';

const FIELD_TYPE_TO_VALUE_TYPE = {
  ID: 'chipSelector',
  SUBMODEL: 'chipSelector',
  STRING: 'textInput',
  TEXT: 'textInput',
  DATE: 'datetimePicker',
  BOOLEAN: 'booleanSelector',
  INTEGER: 'numberInput',
  ALL: null,
};

const EQUAL_OP = { key: 'equal', value: '=' };
const GREATHER_THAN = { key: 'greaterThan', value: '>' };
const GREATHER_EQUAL_THAN = { key: 'greaterOrEqualThan', value: '>=' };
const LOWER_THAN = { key: 'lowerThan', value: '<' };
const LOWER_EQUAL_THAN = { key: 'lowerOrEqualThan', value: '<=' };
const IN_OP = { key: 'in', value: 'IN' };

const FIELD_TYPE_TO_OPERATIONS = {
  ID: [IN_OP],
  SUBMODEL: [IN_OP],
  STRING: [EQUAL_OP],
  TEXT: [EQUAL_OP],
  DATE: [EQUAL_OP],
  BOOLEAN: [EQUAL_OP],
  ALL: [],
  INTEGER: [EQUAL_OP, GREATHER_THAN, GREATHER_EQUAL_THAN, LOWER_THAN, LOWER_EQUAL_THAN],
};

// TODO: Remove this when all columns point to either ID or LID
const ENTITY_TO_COLUMN_TO_RETRIEVE = {
  itemType: 'logicalId',
};

class WhereClause extends Needs {
  constructor(props) {
    super(props);
    const GENERIC_COLUMN_NAME = {
      fnLabel: () => `${this.props.t('Name / ID')}`,
      name: 'name',
      rawValue: o => o.get('name'),
    };
    const GENERIC_COLUMN_ID = {
      fnLabel: () => `${this.props.t('ID')}`,
      name: 'id',
      fHidden: true,
    };

    this.genericCols = [GENERIC_COLUMN_NAME, GENERIC_COLUMN_ID];
    this.state = {
      id: this.props.id,
      multiSelectOps: {},
      ...props.whereClause,
    };
  }

  componentDidMount() {
    this.iNeed({
      url: `/api/entity/${this.props.entityId}`,
      dataPath: `entity_${this.props.entityId}`,
    });
    this.fetchWhereClauseModels();
  }

  componentDidUpdate(prevProps, prevState) {
    this.entity = this.props.data.getIn([`entity_${this.props.entityId}`, 'data']);
    const prevEntity = prevProps.data.getIn([`entity_${this.props.entityId}`, 'data']);
    if (this.entity) {
      this.fields = this.entity.get('fields');
      this.field = this.fields.find(field => field.get('id') === this.state.fieldId);
    }

    if (this.state.fieldId != prevState.fieldId || (!prevEntity && this.entity)) {
      this.fetchWhereClauseModels();
    }
    super.componentDidUpdate(prevProps, prevState);
  }

  fetchWhereClauseModels() {
    const { fieldId, multiSelectOps: multiSelectOps0 } = this.state;
    const { data, entityId } = this.props;
    this.entity = data.getIn([`entity_${entityId}`, 'data']);
    if (!this.entity) return;
    this.fields = this.entity.get('fields');
    this.field = this.fields.find(field => field.get('id') === fieldId);
    if (this.field) {
      const multiSelectOps = {
        ...multiSelectOps0,
        cols: this.genericCols,
        fetchData: {},
      };

      if (this.field.get('type') === 'SUBMODEL') {
        switch (this.field.get('internalName')) {
          case 'tag':
            multiSelectOps.isEntitySelector = true;
            multiSelectOps.dataPath = 'tag';
            multiSelectOps.where = {
              restrictedToEntities: 'tsElement',
            };
            break;
          case 'customerPurchaseOrder':
            multiSelectOps.dataPath = 'customerPurchaseOrders';
            multiSelectOps.itemsFetchUrl = '/api/customerPurchaseOrders';
            break;
          case 'internalPurchaseOrder':
            multiSelectOps.dataPath = 'internalPurchaseOrders';
            multiSelectOps.itemsFetchUrl = '/api/internalPurchaseOrders';
            break;
          case 'itemType':
            multiSelectOps.isEntitySelector = true;
            multiSelectOps.dataPath = 'itemType';
            break;
          case 'currentLocation':
            multiSelectOps.isEntitySelector = true;
            multiSelectOps.dataPath = 'location';
            multiSelectOps.fetchData = {
              filters: { location: { isAlive: true } },
            };
            break;
          default:
            console.log('NOT SUPORTED SUBMODEL');
            break;
        }
      }
      if (this.field.get('type') === 'ID') {
        switch (this.field.get('entityId')) {
          case 'tsElement':
            multiSelectOps.isEntitySelector = true;
            multiSelectOps.dataPath = 'item';
            break;
          case 'tsGeometry':
            multiSelectOps.isEntitySelector = true;
            multiSelectOps.dataPath = 'geometry';
            break;
          case 'tag':
            multiSelectOps.isEntitySelector = true;
            multiSelectOps.dataPath = 'tag';
            break;
          default:
            console.log('NOT SUPORTED SUBMODEL');
            break;
        }
      }
      this.setState({ multiSelectOps });
    }
  }

  render() {
    const { data, entityId } = this.props;
    const { fieldId } = this.state;
    this.entity = data.getIn([`entity_${entityId}`, 'data']);
    if (!this.entity) return null;

    this.fields = this.entity.get('fields');
    this.field = this.fields.find(field => field.get('id') === fieldId);

    return (
      <Grid container spacing={2} alignItems="stretch" direction="row" justify="flex-start">
        {this._renderField()}
        {this._renderOperation()}
        {this._renderValue()}
        {this._renderActions()}
      </Grid>
    );
  }

  _renderField() {
    const { disabled, t } = this.props;
    const { fieldId } = this.state;

    return (
      <Select
        disabled={disabled}
        label={fieldId ? undefined : t('Select filter')}
        className="m-2"
        value={fieldId}
        onChange={this._onChangeFieldId}
        fullWidth
        options={this.fields.map(field => {
          return { value: field.get('id'), label: field.get('name') };
        })}
      />
    );
  }

  _renderOperation() {
    const { disabled } = this.props;
    const { fieldId } = this.state;
    if (!fieldId) return null;
    const fieldType = this.field.get('type');
    const operations = FIELD_TYPE_TO_OPERATIONS[fieldType];
    if (!operations.length) return null;

    return (
      <Select
        disabled={disabled || operations.length === 1}
        className="m-2"
        value={this.state.operation}
        onChange={this._onChangeOperation}
        label={'Operation'}
        fullWidth
        options={operations.map(op => ({ value: op.key, label: op.value }))}
      />
    );
  }

  _renderValue() {
    const { t, disabled, data, app } = this.props;
    const { fieldId, multiSelectOps, value } = this.state;
    if (!fieldId) return null;
    const fieldType = this.field.get('type');
    const internalName = this.field.get('internalName');
    const columnToRetrieve = ENTITY_TO_COLUMN_TO_RETRIEVE[internalName] || 'id';
    const valueType = FIELD_TYPE_TO_VALUE_TYPE[fieldType];
    if (!valueType) return null;

    switch (valueType) {
      case 'chipSelector':
        const { cols, itemsFetchUrl, dataPath, fetchData, isEntitySelector, where } = multiSelectOps;
        if (isEntitySelector) {
          return (
            <WrapperEntitySelector
              label={'Values'}
              value={value}
              searchComponent={generateSearchEntitySelector(dataPath, { where })}
              onChange={this._onChangeMultiSelect}
              isReadOnly={disabled}
              multiple
              seeAllSelected
            />
          );
        }
        if (!this.entity || !cols) return;
        return (
          <QueryPaginatedSelection
            data={data}
            app={app}
            selectedIds={value}
            columns={cols}
            pkColumn={'id'}
            itemsFetchUrl={itemsFetchUrl}
            fetchData={fetchData}
            itemsDataPath={dataPath}
            fnChipCaption={this.fnChipCaption}
            onChangeSelected={this._onChangeMultiSelect}
            title={`${t('Values')}`}
            disabled={disabled}
            columnToRetrieve={columnToRetrieve}
          />
        );
      case 'textInput':
      case 'dateTimePicker':
        return <TextInput disabled={disabled} className="m-2" label="Value" value={value} onChangeValue={this._onChangeValue} />;
      case 'booleanSelector':
        return (
          <Select
            disabled={disabled}
            className="m-2"
            value={value}
            onChange={this._onChangeSelectValue}
            label={`${t('Value')}`}
            fullWidth
            options={[
              { value: true, label: `${t('True')}` },
              { value: false, label: `${t('False')}` },
            ]}
          />
        );
      case 'numberInput':
        return (
          <TextInput
            type="number"
            disabled={disabled}
            className="m-2"
            label="Value"
            value={value}
            onChangeValue={this._onChangeValue}
          />
        );
      default:
        break;
    }
    return null;
  }

  _renderActions = () => {
    return (
      <IconButton disabled={this.props.disabled} onClick={this._onRemoveWhereClause}>
        <Icon icon="action-delete" />
      </IconButton>
    );
  };

  _onChangeFieldId = value => {
    const fields0 = this.props.data.getIn([`entity_${this.props.entityId}`, 'data', 'fields']);
    const fields = fields0 ? fields0?.toJS() : [];
    const field = fields.find(field0 => field0.id === value);
    const operations = field ? FIELD_TYPE_TO_OPERATIONS[field.type] : null;
    const operation = operations?.length === 1 ? operations[0].key : null;
    this.setState({
      fieldId: value,
      operation,
      value: null,
    });
    this.props.onChange({
      id: this.props.whereClause.id,
      fieldId: value,
      operation,
      value: null,
    });
  };

  _onChangeOperation = value => {
    this.setState({
      operation: value,
    });
    this.props.onChange({
      id: this.props.whereClause.id,
      fieldId: this.state.fieldId,
      operation: value,
      value: this.state.value,
    });
  };


  _onChangeValue = ( value) => {
    this.setState({
      value: value,
    });
    this.props.onChange({
      id: this.props.whereClause.id,
      fieldId: this.state.fieldId,
      operation: this.state.operation,
      value: value,
    });
  };

  _onChangeSelectValue = value => {
    this.setState({
      value: value,
    });
    this.props.onChange({
      id: this.props.whereClause.id,
      fieldId: this.state.fieldId,
      operation: this.state.operation,
      value: value,
    });
  };

  _onRemoveWhereClause = () => {
    this.props.onChange({
      id: this.props.whereClause.id,
      isAlive: false,
    });
  };

  _onChangeMultiSelect = selected => {
    this.props.onChange({
      id: this.props.whereClause.id,
      fieldId: this.state.fieldId,
      operation: this.state.operation,
      value: selected,
    });
    this.setState({ value: selected });
  };

  fnChipCaption = item => item.get('name');
}

export default withTranslation()(WhereClause);
